<script setup lang="ts">
import type { NuxtError } from '#app'

/**
 * Nuxt error page in the template's 404 style: the illustration, one line,
 * a pill button, a hairline and the page CTA. A 404 sends people home; any
 * other error offers a retry first (clearError re-renders the same route).
 */
const props = defineProps<{
  error: NuxtError
}>()

const { t } = useI18n()

const isNotFound = computed(() => props.error?.statusCode === 404)

const title = computed(() => (isNotFound.value ? t('sofax.notFound.title') : t('sofax.notFound.error')))
const text = computed(() => (isNotFound.value ? t('sofax.notFound.text') : t('sofax.notFound.errorText')))

useSeoMeta({
  title: () => (isNotFound.value ? t('common.notFound.pageTitle') : t('sofax.notFound.error')),
  robots: 'noindex, follow',
})

function goHome() {
  clearError({ redirect: '/' })
}

function retry() {
  clearError()
}
</script>

<template>
  <NuxtLayout name="sell">
    <section class="sofax bg-white dark:bg-gray-950">
      <div class="section-space">
        <div class="shell">
          <div class="mx-auto max-w-3xl text-center xl:max-w-4xl">
            <img
              src="/sofax/404.svg"
              alt=""
              aria-hidden="true"
              width="629"
              height="270"
              class="mx-auto h-auto max-w-full"
            >

            <h2 class="mb-6 mt-10 md:mt-[3.75rem] lg:mt-20">{{ title }}</h2>
            <p class="lead mx-auto mb-10 max-w-2xl">{{ text }}</p>

            <div class="flex flex-wrap items-center justify-center gap-4">
              <SofaxBtn v-if="!isNotFound" :label="t('sofax.notFound.retry')" variant="accent" @click="retry" />
              <SofaxBtn
                :label="t('sofax.notFound.back')"
                :variant="isNotFound ? 'accent' : 'outline'"
                @click="goHome"
              />
            </div>

            <div v-if="!isNotFound && error?.statusCode" class="mt-8 text-sm text-muted dark:text-gray-400">
              {{ error.statusCode }}
            </div>
          </div>
        </div>
      </div>

      <div class="separator" />
    </section>

    <SofaxPageCta />
  </NuxtLayout>
</template>
